/**
 * Agent Sandbox - Test and iterate on agents before using them in pipelines
 */

import React, { useState, useEffect, useRef } from 'react';
import { AgentConfig } from '../types/agent';
import { ExecutionStatus } from '../types/pipeline';
import { apiClient, pipelineApi, API_BASE_URL, getApiKey } from '../api/client';
import { groupAgentsBySlug } from '../utils/agentVersions';
import { ArrowLeftIcon, CheckIcon, CloseIcon, InfoIcon, PlayIcon } from './icons';
import './AgentSandbox.css';

// Task types testable via the live dispatch path (POST /agents/{id}/test-live), which
// routes through the real WorkflowExecutor instead of rendering prompt_config (blank for
// a default agent). Mirrors _LIVE_TESTABLE_TASK_TYPES / _PAIRWISE_LIVE_TESTABLE_TASK_TYPES
// in raiqon/api/routers/agents.py - keep these two allowlists in sync with that file.
const SINGLE_ITEM_LIVE_TESTABLE_TASK_TYPES = new Set([
  'classification',
  'test_condition',
  'weak_words',
  'ai_review',
  'summary_description_inconsistency',
]);
const PAIRWISE_LIVE_TESTABLE_TASK_TYPES = new Set([
  'cross_item_analysis',
  'traceability_analysis',
  'redundancy_check',
]);

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  metadata?: any;
}

interface AgentSandboxProps {
  initialAgentId?: string;
  onClose?: () => void;
}

// Drop blank entries so an untouched field doesn't override an item-derived
// value: the backend treats any key present in `context` as a manual override,
// regardless of whether its value is empty.
const nonEmptyContext = (fields: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(fields).filter(([, v]) => v.trim() !== ''));

// Resolve a pasted ALM item URL (e.g. .../cb/issue/45341587) or bare ALM item id to the
// internal item UUID the sandbox endpoints take.
export async function resolveItemReference(reference: string): Promise<string> {
  try {
    const result = await pipelineApi.resolveAlmUrl(reference, 'item_id');
    if (result.not_found || result.scope?.type_id !== 'ScopeFromSourceIds') {
      throw new Error(`Item not found: ${reference}`);
    }
    return result.scope.source_ids[0];
  } catch (err: any) {
    throw new Error(err.response?.data?.detail || err.message);
  }
}

export const AgentSandbox: React.FC<AgentSandboxProps> = ({ initialAgentId, onClose }) => {
  const [agents, setAgents] = useState<AgentConfig[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentConfig | null>(null);
  const [mode] = useState<'test' | 'chat'>('test');
  const [showHelp, setShowHelp] = useState(false);

  // Where test context comes from: typed in by hand, or fetched from an ALM item (URL or ID),
  // with any filled-in fields still overriding the item's values. Shared by test + chat modes.
  const [contextSource, setContextSource] = useState<'manual' | 'item'>('manual');
  const [itemReference, setItemReference] = useState('');

  // Test mode state
  const [contextFields, setContextFields] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<any | null>(null);

  // Live-test mode (default agents only): hardcoded task-dispatch path, no prompt to preview.
  const [liveItemId, setLiveItemId] = useState('');
  const [liveRelatedItemId, setLiveRelatedItemId] = useState('');
  const [liveTestResult, setLiveTestResult] = useState<any | null>(null);

  // Chat mode state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [chatContext, setChatContext] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load agents
  useEffect(() => {
    loadAgents();
  }, []);

  // Select initial agent if provided. Fetch the full agent (not the list summary)
  // so context_fields, prompt, and output are populated — listAgents() returns
  // placeholders for those.
  useEffect(() => {
    if (initialAgentId && agents.length > 0) {
      if (agents.some(a => a.id === initialAgentId)) {
        selectAgentById(initialAgentId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAgentId, agents]);

  // Auto-scroll chat
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadAgents = async () => {
    try {
      const loadedAgents = await apiClient.listAgents();
      setAgents(loadedAgents);
    } catch (err) {
      setError(`Failed to load agents: ${err}`);
    }
  };

  const initializeContextFields = (agent: AgentConfig) => {
    const fields: Record<string, string> = {};
    agent.context_fields.forEach(field => {
      fields[field.name] = field.default_value || '';
    });
    setContextFields(fields);
    setChatContext(fields);
  };

  const selectAgentById = async (agentId: string) => {
    setTestResult(null);
    setLiveTestResult(null);
    setLiveItemId('');
    setLiveRelatedItemId('');
    setMessages([]);
    setError(null);
    try {
      setLoading(true);
      // listAgents() returns summary rows with placeholder context_fields/prompt/output.
      // We MUST fetch the full agent to render context inputs and rendered prompts.
      const fullAgent = await apiClient.getAgent(agentId);
      setSelectedAgent(fullAgent);
      initializeContextFields(fullAgent);
    } catch (err) {
      setError(`Failed to load agent: ${err}`);
      setSelectedAgent(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentSelect = (agentId: string) => {
    if (!agentId) {
      setSelectedAgent(null);
      setTestResult(null);
      setLiveTestResult(null);
      setLiveItemId('');
      setLiveRelatedItemId('');
      setMessages([]);
      setError(null);
      return;
    }
    selectAgentById(agentId);
  };

  // Group the flat (slug, version) rows from listAgents() into one entry per
  // agent, so the primary dropdown offers one choice per agent - the version
  // dropdown below it is where a specific older/newer version gets picked.
  const agentGroups = groupAgentsBySlug(agents);
  const selectedGroup = selectedAgent
    ? agentGroups.find(g => g.key === (selectedAgent.slug || selectedAgent.id))
    : undefined;

  const handleAgentGroupSelect = (groupKey: string) => {
    if (!groupKey) {
      handleAgentSelect('');
      return;
    }
    const group = agentGroups.find(g => g.key === groupKey);
    // Default to the latest version when switching agents.
    handleAgentSelect(group?.versions[0]?.id ?? '');
  };

  const isDefaultAgent = selectedAgent?.is_default ?? false;
  const agentTaskType = selectedAgent?.task_type;
  const isPairwiseLiveTestable = isDefaultAgent && !!agentTaskType && PAIRWISE_LIVE_TESTABLE_TASK_TYPES.has(agentTaskType);
  const isSingleItemLiveTestable = isDefaultAgent && !!agentTaskType && SINGLE_ITEM_LIVE_TESTABLE_TASK_TYPES.has(agentTaskType);
  const isLiveTestable = isSingleItemLiveTestable || isPairwiseLiveTestable;

  const handleTest = async () => {
    if (!selectedAgent) return;

    const reference = contextSource === 'item' ? itemReference.trim() : '';
    if (contextSource === 'item' && !reference) {
      setError('Enter an item URL or ID, or switch to manual input');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setTestResult(null);
      const trimmedItemId = reference ? await resolveItemReference(reference) : '';

      // Use streaming endpoint for real-time responses. Hit the API host directly
      // (same base URL as axios) — the Vite /api proxy doesn't strip the prefix.
      const response = await fetch(`${API_BASE_URL}/agents/testing/test/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey(),
        },
        body: JSON.stringify({
          agent_id: selectedAgent.id,
          context: nonEmptyContext(contextFields),
          ...(trimmedItemId ? { item_id: trimmedItemId } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Test failed');
      }

      // Read streaming response. Buffer partial lines across reads: a single NDJSON
      // line (e.g. a large "thinking" payload) isn't guaranteed to arrive in one
      // reader.read() call, so splitting each read's chunk on '\n' in isolation can
      // hand JSON.parse a truncated line.
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      let fullResponse = '';
      let fullThinking = '';
      let buffer = '';

      const processLine = (line: string) => {
        if (!line.trim()) return;
        try {
          const data = JSON.parse(line);

          if (data.type === 'metadata') {
            // Initialize test result with metadata
            setTestResult({
              agent_id: data.agent_id,
              agent_name: data.agent_name,
              input_context: contextFields,
              rendered_prompt: data.rendered_prompt,
              model_configuration: data.model,
              response: '',
              thinking: '',
              metadata: { status: 'streaming' },
            });
          } else if (data.type === 'thinking') {
            fullThinking += data.content;
            setTestResult((prev: any) => prev ? {
              ...prev,
              thinking: fullThinking,
            } : null);
          } else if (data.type === 'chunk') {
            fullResponse += data.content;
            // Update response in real-time
            setTestResult((prev: any) => prev ? {
              ...prev,
              response: fullResponse,
            } : null);
          } else if (data.type === 'complete') {
            // Finalize response
            setTestResult((prev: any) => prev ? {
              ...prev,
              metadata: { ...prev.metadata, ...data, status: 'complete' },
            } : null);
          } else if (data.type === 'error') {
            throw new Error(data.error);
          }
        } catch (parseErr) {
          console.error('Failed to parse chunk:', line, parseErr);
        }
      };

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          lines.forEach(processLine);
        }
        if (buffer.trim()) processLine(buffer);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLiveTest = async () => {
    if (!selectedAgent) return;

    if (!liveItemId.trim() || (isPairwiseLiveTestable && !liveRelatedItemId.trim())) {
      setError(isPairwiseLiveTestable ? 'Enter both item URLs or IDs' : 'Enter an item URL or ID');
      return;
    }

    setLoading(true);
    setError(null);
    setLiveTestResult(null);

    try {
      const trimmedItemId = await resolveItemReference(liveItemId.trim());
      const relatedId = isPairwiseLiveTestable ? await resolveItemReference(liveRelatedItemId.trim()) : undefined;
      const { execution_id, task_type, result_id } = await apiClient.testAgentLive(selectedAgent.id, {
        item_id: trimmedItemId,
        ...(relatedId ? { related_item_id: relatedId } : {}),
      });

      // Poll the completion barrier until it reaches a terminal state.
      for (;;) {
        const execution = await apiClient.getExecutionStatus(execution_id, false);
        if (execution.status === ExecutionStatus.FAILED) {
          throw new Error('Live test execution failed');
        }
        if (execution.status === ExecutionStatus.COMPLETED) {
          if (!result_id) {
            throw new Error('Execution completed but produced no result reference');
          }
          const { result } = await apiClient.getTestLiveResult(task_type, result_id);
          setLiveTestResult(result);
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedAgent || !currentMessage.trim()) return;

    const reference = contextSource === 'item' ? itemReference.trim() : '';

    const userMessage: ChatMessage = {
      role: 'user',
      content: currentMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setLoading(true);
    setError(null);

    try {
      const trimmedItemId = reference ? await resolveItemReference(reference) : '';
      // Use streaming endpoint for real-time chat. Same base URL as axios so
      // we bypass the Vite /api proxy (which doesn't strip the prefix).
      const response = await fetch(`${API_BASE_URL}/agents/testing/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey(),
        },
        body: JSON.stringify({
          agent_id: selectedAgent.id,
          messages: [...messages, userMessage].map(m => ({
            role: m.role,
            content: m.content,
          })),
          context: nonEmptyContext(chatContext),
          ...(trimmedItemId ? { item_id: trimmedItemId } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Chat failed');
      }

      // Read streaming response. Buffer partial lines across reads: a single NDJSON
      // line (e.g. a large "thinking" payload) isn't guaranteed to arrive in one
      // reader.read() call, so splitting each read's chunk on '\n' in isolation can
      // hand JSON.parse a truncated line.
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      let fullContent = '';
      let fullThinking = '';
      let chatMetadata: any = {};
      let buffer = '';

      // Create placeholder assistant message
      const assistantMessageId = Date.now();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        metadata: { streaming: true, id: assistantMessageId },
      }]);

      const processLine = (line: string) => {
        if (!line.trim()) return;
        try {
          const data = JSON.parse(line);

          if (data.type === 'metadata') {
            chatMetadata = data;
          } else if (data.type === 'thinking') {
            fullThinking += data.content;
            setMessages(prev => prev.map(msg =>
              msg.metadata?.id === assistantMessageId
                ? { ...msg, metadata: { ...msg.metadata, thinking: fullThinking } }
                : msg
            ));
          } else if (data.type === 'chunk') {
            fullContent += data.content;
            // Update assistant message in real-time
            setMessages(prev => prev.map(msg =>
              msg.metadata?.id === assistantMessageId
                ? { ...msg, content: fullContent }
                : msg
            ));
          } else if (data.type === 'complete') {
            // Finalize message
            setMessages(prev => prev.map(msg =>
              msg.metadata?.id === assistantMessageId
                ? { ...msg, metadata: { ...chatMetadata, ...msg.metadata, ...data, streaming: false } }
                : msg
            ));
          } else if (data.type === 'error') {
            throw new Error(data.error);
          }
        } catch (parseErr) {
          console.error('Failed to parse chat chunk:', line, parseErr);
        }
      };

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          lines.forEach(processLine);
        }
        if (buffer.trim()) processLine(buffer);
      }
    } catch (err: any) {
      setError(err.message);
      // Remove the placeholder message on error
      setMessages(prev => prev.filter(msg => !msg.metadata?.streaming));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const resetChat = () => {
    setMessages([]);
    setError(null);
  };

  return (
    <div className="agent-sandbox">
      <div className="sandbox-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
          {onClose && (
            <button
              onClick={onClose}
              className="back-button"
              style={{
                background: 'none',
                border: '2px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: 'fit-content',
              }}
              title="Back to agent list"
            >
              <ArrowLeftIcon /> Back
            </button>
          )}
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 4px 0' }}>Agent Sandbox</h2>
            <p className="sandbox-subtitle" style={{ margin: 0 }}>Test your agents before using them in pipelines</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <button
            className="help-button"
            onClick={() => setShowHelp(!showHelp)}
            title="How to test"
            style={{
              background: 'none',
              border: '2px solid var(--border-color)',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-secondary)',
            }}
          >
            ?
          </button>
        </div>
      </div>

      {showHelp && (
        <div className="sandbox-help">
          <div className="help-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <strong><InfoIcon /> How to test</strong>
              <button
                onClick={() => setShowHelp(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0',
                  color: 'var(--text-secondary)',
                }}
              >
                ×
              </button>
            </div>
            <ol>
              <li><strong>Select an agent</strong> from the dropdown below</li>
              <li><strong>Provide context</strong> - Fill in the required fields for your agent</li>
              <li><strong>Test!</strong> Click "Test Agent"</li>
              <li><strong>Review results</strong> - See the response, rendered prompts, and model config</li>
            </ol>
          </div>
        </div>
      )}

      <div className="sandbox-controls">
        <div className="control-group">
          <label>Select Agent</label>
          <select
            value={selectedGroup?.key || ''}
            onChange={(e) => handleAgentGroupSelect(e.target.value)}
            className="agent-select"
          >
            <option value="">Choose an agent...</option>
            {agentGroups.map(group => (
              <option key={group.key} value={group.key}>
                {group.versions[0].name} ({group.versions[0].model.model_name})
              </option>
            ))}
          </select>
        </div>

        {selectedGroup && selectedGroup.versions.length > 1 && (
          <div className="control-group">
            <label>Version</label>
            <select
              value={selectedAgent?.id || ''}
              onChange={(e) => handleAgentSelect(e.target.value)}
              className="version-select"
            >
              {selectedGroup.versions.map(v => (
                <option key={v.id} value={v.id}>
                  v{v.version}{v.id === selectedGroup.versions[0].id ? ' (latest)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedAgent && (
          <>
            {/* Chat Mode tab is hidden - disabled pending a fix, see chat_agent_stream in agents.py */}
            <div className="agent-info">
              <span className="info-badge">{selectedAgent.category || 'Custom'}</span>
              <span className="info-badge">{selectedAgent.model.provider}</span>
              <span className="info-badge">temp: {selectedAgent.model.temperature}</span>
            </div>
          </>
        )}
      </div>

      {selectedAgent && (
        <div className="sandbox-content">
          {mode === 'test' ? (
            // Test Mode
            <div className="test-mode">
              {isDefaultAgent ? (
                <div className="test-input">
                  <h3>Live Test</h3>
                  <p className="hint">
                    This is a built-in default agent - it has no prompt to preview, so it's
                    tested by dispatching the real hardcoded task against a live item.
                  </p>

                  {isLiveTestable ? (
                    <>
                      <div className="context-field">
                        <label>Item URL or ID<span className="required">*</span></label>
                        <input
                          type="text"
                          value={liveItemId}
                          onChange={(e) => setLiveItemId(e.target.value)}
                          placeholder="e.g. https://codebeamer.example.com/cb/issue/45341587"
                          className="item-id-input"
                        />
                      </div>
                      {isPairwiseLiveTestable && (
                        <div className="context-field">
                          <label>Related Item URL or ID<span className="required">*</span></label>
                          <input
                            type="text"
                            value={liveRelatedItemId}
                            onChange={(e) => setLiveRelatedItemId(e.target.value)}
                            placeholder="URL or ID of the related item to compare against"
                            className="item-id-input"
                          />
                        </div>
                      )}

                      <button
                        onClick={handleLiveTest}
                        disabled={loading}
                        className="btn-primary btn-test"
                      >
                        {loading ? 'Running...' : <><PlayIcon /> Run Live Test</>}
                      </button>
                    </>
                  ) : (
                    <div className="no-fields">
                      Task type "{agentTaskType}" needs cross-item or aggregate context that a
                      single-item live test can't provide, so this default agent can't be tested
                      in isolation.
                    </div>
                  )}
                </div>
              ) : (
                <div className="test-input">
                  <h3>Context Input</h3>
                  <div className="mode-tabs context-source-tabs">
                    <button
                      type="button"
                      className={contextSource === 'manual' ? 'active' : ''}
                      onClick={() => setContextSource('manual')}
                    >
                      Manual input
                    </button>
                    <button
                      type="button"
                      className={contextSource === 'item' ? 'active' : ''}
                      onClick={() => setContextSource('item')}
                    >
                      From ALM item
                    </button>
                  </div>

                  {contextSource === 'item' ? (
                    <>
                      <div className="context-field">
                        <label>Item URL or ID<span className="required">*</span></label>
                        <input
                          type="text"
                          value={itemReference}
                          onChange={(e) => setItemReference(e.target.value)}
                          placeholder="e.g. https://codebeamer.example.com/cb/issue/45341587"
                          className="item-id-input"
                        />
                        <small>The agent's context fields are filled from this item.</small>
                      </div>
                      <p className="hint">Optional overrides - leave a field empty to use the item's value</p>
                    </>
                  ) : (
                    <p className="hint">Provide values for the agent's context fields</p>
                  )}

                  {selectedAgent.context_fields.length > 0 ? (
                    <div className="context-fields">
                      {selectedAgent.context_fields.map(field => (
                        <div key={field.name} className="context-field">
                          <label>
                            {field.name}
                            {field.required && <span className="required">*</span>}
                          </label>
                          {field.description && <small>{field.description}</small>}
                          <textarea
                            value={contextFields[field.name] || ''}
                            onChange={(e) => setContextFields({
                              ...contextFields,
                              [field.name]: e.target.value
                            })}
                            placeholder={contextSource === 'item' ? "Leave empty to use the item's value" : `Type or paste ${field.name}...`}
                            rows={6}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-fields">
                      This agent has no context fields defined.
                    </div>
                  )}

                  <button
                    onClick={handleTest}
                    disabled={loading}
                    className="btn-primary btn-test"
                  >
                    {loading ? 'Testing...' : <><PlayIcon /> Test Agent</>}
                  </button>
                </div>
              )}

              {isDefaultAgent ? (
                liveTestResult && (
                  <div className="test-output">
                    <h3>Agent Response</h3>
                    <div className="output-section">
                      <h4>Result</h4>
                      <pre className="response-content">{JSON.stringify(liveTestResult, null, 2)}</pre>
                    </div>
                  </div>
                )
              ) : (
                testResult && (
                  <div className="test-output">
                    <h3>Agent Response</h3>

                    {testResult.thinking && (
                      <div className="output-section thinking-section">
                        <h4>Thinking</h4>
                        <pre className="thinking-content">{testResult.thinking}</pre>
                      </div>
                    )}

                    <div className="output-section">
                      <h4>Response</h4>
                      <pre className="response-content">{testResult.response}</pre>
                    </div>

                    {testResult.metadata?.validation && (
                      <div className={`output-section validation-section ${testResult.metadata.validation.valid ? 'valid' : 'invalid'}`}>
                        <h4>Schema Validation</h4>
                        {testResult.metadata.validation.valid ? (
                          <p className="validation-status"><CheckIcon /> Response matches the output schema</p>
                        ) : (
                          <>
                            <p className="validation-status"><CloseIcon /> Response does not match the output schema</p>
                            <pre>{JSON.stringify(testResult.metadata.validation.errors, null, 2)}</pre>
                          </>
                        )}
                      </div>
                    )}

                    <div className="output-section">
                      <h4>Rendered Prompts</h4>
                      <div className="prompt-display">
                        <div className="prompt-part">
                          <strong>System:</strong>
                          <pre>{testResult.rendered_prompt.system}</pre>
                        </div>
                        <div className="prompt-part">
                          <strong>User:</strong>
                          <pre>{testResult.rendered_prompt.user}</pre>
                        </div>
                      </div>
                    </div>

                    <div className="output-section">
                      <h4>Model Configuration</h4>
                      <pre>{JSON.stringify(testResult.model_configuration, null, 2)}</pre>
                    </div>

                    {testResult.metadata && (
                      <div className="output-section">
                        <h4>Metadata</h4>
                        <pre>{JSON.stringify(testResult.metadata, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          ) : (
            // Chat Mode
            <div className="chat-mode">
              <div className="chat-sidebar">
                <h3>Chat Context</h3>
                <p className="hint">Persistent context for the conversation</p>

                {selectedAgent.context_fields.length > 0 ? (
                  <div className="context-fields-compact">
                    {selectedAgent.context_fields.map(field => (
                      <div key={field.name} className="context-field-compact">
                        <label>{field.name}</label>
                        <input
                          type="text"
                          value={chatContext[field.name] || ''}
                          onChange={(e) => setChatContext({
                            ...chatContext,
                            [field.name]: e.target.value
                          })}
                          placeholder={field.description}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-fields-compact">
                    No context fields
                  </div>
                )}

                <button onClick={resetChat} className="btn-secondary btn-reset">
                  Reset Chat
                </button>
              </div>

              <div className="chat-main">
                <div className="chat-messages">
                  {messages.length === 0 ? (
                    <div className="chat-empty">
                      <p>Start a conversation with {selectedAgent.name}</p>
                      <small>Messages will appear here</small>
                    </div>
                  ) : (
                    messages.map((msg, idx) => (
                      <div key={idx} className={`chat-message ${msg.role}`}>
                        <div className="message-header">
                          <span className="message-role">
                            {msg.role === 'user' ? 'You' : selectedAgent.name}
                          </span>
                          <span className="message-time">
                            {msg.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                        {msg.metadata?.thinking && (
                          <div className="message-thinking">
                            <strong>Thinking:</strong>
                            <pre>{msg.metadata.thinking}</pre>
                          </div>
                        )}
                        <div className="message-content">
                          {msg.content}
                        </div>
                        {msg.metadata && (
                          <div className="message-metadata">
                            Turn {msg.metadata.turn}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="chat-input-area">
                  <textarea
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message... (Shift+Enter for new line)"
                    rows={3}
                    disabled={loading}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={loading || !currentMessage.trim()}
                    className="btn-primary btn-send"
                  >
                    {loading ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="sandbox-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!selectedAgent && (
        <div className="sandbox-placeholder">
          <p>Select an agent above to start testing</p>
        </div>
      )}
    </div>
  );
};
